// @ts-nocheck
import { Match, Switch, on, onCleanup, Show, splitProps, onMount, For, createSignal, createMemo, createEffect } from "solid-js";
import * as config from './config';
import socket, { saveCell } from "./socket";
import { createStore } from "solid-js/store";
import { useUserContext } from "./UserContext";
import { NavDropdown, Loader } from "./Components";
import ProjectSummary from "./ProjectSummary";
import ProjectSettings from "./ProjectSettings";
import ProjectHistory from "./ProjectHistory";
import ProjectNotifications from "./ProjectNotifications";
import { calculateStudentGrades, getScoreBadgeClass } from './gradeUtils';

const normalizeStudentName = (name) => (name || '').trim().toLocaleLowerCase('fr-FR');

// A cell holds two independent, separately-lockable fields for one grading item:
// a numeric "Note" (0-4) and a free-text "Feedback" comment.
const Cell = (props) => {
  const [editingNote, setEditingNote] = createSignal(false);
  const [editingComment, setEditingComment] = createSignal(false);
  let noteInputRef;
  let commentInputRef;

  const inputId = createMemo(() => {
    return `input-${props.project_id}-${props.page}-${props.key_note}`;
  });

  const cell_comment = createMemo(() => {
    return {
      project_id: props.project_id,
      page: props.page,
      key: props.key_comment,
    };
  });
  const cell_note = createMemo(() => {
    return {
      project_id: props.project_id,
      page: props.page,
      key: props.key_note,
    };
  });

  const isNoteLocked = createMemo(() => !!props.cell_note.lockedBy && !editingNote());
  const isCommentLocked = createMemo(() => !!props.cell_comment.lockedBy && !editingComment());

  const adjustCommentHeight = () => {
    if (!commentInputRef) return;
    commentInputRef.style.height = 'auto';
    commentInputRef.style.height = `${Math.max(38, commentInputRef.scrollHeight)}px`;
  };

  createEffect(() => {
    const val = props.cell_note.value ?? '';
    if (!editingNote() && noteInputRef) {
      noteInputRef.value = val;
    }
  });

  createEffect(() => {
    const val = props.cell_comment.value || '';
    if (commentInputRef) {
      if (!editingComment()) {
        commentInputRef.value = val;
      }
      adjustCommentHeight();
    }
  });

  onMount(() => {
    adjustCommentHeight();

    // Utilisation d'un ResizeObserver pour recalculer automatiquement
    // la hauteur dès que la largeur de l'élément change (redimensionnement de fenêtre, panneau latéral, etc.)
    if (typeof ResizeObserver !== 'undefined' && commentInputRef) {
      let lastWidth = commentInputRef.offsetWidth;
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newWidth = entry.contentRect.width;
          if (newWidth !== lastWidth) {
            lastWidth = newWidth;
            adjustCommentHeight();
          }
        }
      });
      ro.observe(commentInputRef);
      onCleanup(() => ro.disconnect());
    } else {
      const onResize = () => adjustCommentHeight();
      window.addEventListener('resize', onResize);
      onCleanup(() => window.removeEventListener('resize', onResize));
    }
  });

  const onFocusNote = () => {
    if(props.cell_note.lockedBy) return;
    setEditingNote(true);
    props.lock(cell_note());
  };
  const onBlurNote = () => {
    if(!editingNote()) return;
    setEditingNote(false);
    props.unlock(cell_note());
  };
  const onFocusComment = () => {
    if(props.cell_comment.lockedBy) return;
    setEditingComment(true);
    props.lock(cell_comment());
  };
  const onBlurComment = () => {
    if(!editingComment()) return;
    setEditingComment(false);
    props.unlock(cell_comment());
  };

  onCleanup(() => {
    if(editingNote()) {
      props.unlock(cell_note());
    }
    if(editingComment()) {
      props.unlock(cell_comment());
    }
  });

  return (
    <div class="cell-row">
      <div class="cell-label">
        <label for={inputId() + '-note'}>{props.label}</label>
      </div>
      <div
        class="cell-note-wrapper"
        classList={{
          'locked': isNoteLocked(),
          'editing': editingNote(),
        }}
      >
        <input
          ref={noteInputRef}
          id={inputId() + '-note'}
          aria-label={`Note - ${props.label}`}
          title={isNoteLocked() ? `Verrouillé par ${props.cell_note.lockedBy}` : 'Note (0 à 4)'}
          readOnly={!editingNote()}
          disabled={isNoteLocked()}
          class="form-control"
          type="number"
          min={0}
          max={4}
          step="any"
          placeholder="-"
          value={props.cell_note.value ?? ''}
          onFocus={onFocusNote}
          onBlur={onBlurNote}
          onInput={(e) => {
            props.onSave(props.project_id, props.page, props.key_note, e.currentTarget.value);
          }}
        />
        <Show when={isNoteLocked()}>
          <span class="lock-badge">
            <i class="bi bi-lock-fill"></i>
            {props.cell_note.lockedBy}
          </span>
        </Show>
      </div>
      <div
        class="cell-comment-wrapper"
        classList={{
          'locked': isCommentLocked(),
          'editing': editingComment(),
        }}
      >
        <textarea
          ref={commentInputRef}
          id={inputId() + '-comment'}
          aria-label={`Feedback - ${props.label}`}
          title={isCommentLocked() ? `Verrouillé par ${props.cell_comment.lockedBy}` : 'Commentaire & feedback'}
          readOnly={!editingComment()}
          disabled={isCommentLocked()}
          rows={1}
          placeholder="Commentaire / justification (optionnel)..."
          value={props.cell_comment.value || ''}
          onFocus={onFocusComment}
          onBlur={onBlurComment}
          onInput={(e) => {
            adjustCommentHeight();
            props.onSave(props.project_id, props.page, props.key_comment, e.currentTarget.value);
          }}
          class="form-control auto-resize-textarea"
        ></textarea>
        <Show when={isCommentLocked()}>
          <span class="lock-badge">
            <i class="bi bi-lock-fill"></i>
            {props.cell_comment.lockedBy}
          </span>
        </Show>
      </div>
    </div>
  );
};

export const ProjectView = (props) => {
  const [currentPage, setCurrentPage] = createSignal('Summary');
  const [userStore, setUserStore, userActions] = useUserContext();
  
  // Modals & Dialogs state
  const [showAddStudentModal, setShowAddStudentModal] = createSignal(false);
  const [newStudentName, setNewStudentName] = createSignal('');
  const [showRenameStudentModal, setShowRenameStudentModal] = createSignal(false);
  const [renameStudentName, setRenameStudentName] = createSignal('');
  const [addStudentError, setAddStudentError] = createSignal('');
  const [renameStudentError, setRenameStudentError] = createSignal('');
  const [studentActionPending, setStudentActionPending] = createSignal('');
  const [showInviteModal, setShowInviteModal] = createSignal(false);
  const [showEmailModal, setShowEmailModal] = createSignal(false);
  const [copiedLink, setCopiedLink] = createSignal(false);

  // Directory search state for student email autofill
  const [dirResults, setDirResults] = createSignal([]);
  const [dirQuery, setDirQuery] = createSignal('');
  const [dirSearchInput, setDirSearchInput] = createSignal('');
  const [dirLoading, setDirLoading] = createSignal(false);
  const [dirError, setDirError] = createSignal('');
  const [editingEmail, setEditingEmail] = createSignal(false);

  const project = createMemo(() => {
    return userStore.openedProject[props.project_id];
  });

  const studentsList = createMemo(() => {
    if (!project() || !project().pages) return [];
    return Object.keys(project().pages);
  });

  const addNameConflict = createMemo(() => {
    const candidate = normalizeStudentName(newStudentName());
    return !!candidate && studentsList().some((name) => normalizeStudentName(name) === candidate);
  });

  const renameNameConflict = createMemo(() => {
    const candidate = normalizeStudentName(renameStudentName());
    const currentStudent = userStore.lastSelectedStudent;
    return !!candidate && studentsList().some((name) => (
      name !== currentStudent && normalizeStudentName(name) === candidate
    ));
  });

  const studentGrades = createMemo(() => calculateStudentGrades(project(), currentPage()));

  createEffect(() => {
    if(!project()) return;

    const pages = Object.keys(project().pages || {});
    let m = userStore.page.match(/^\/projects\/(\d+)\/(.+)$/);
    if(m && parseInt(m[1]) == project().id && (
      pages.includes(m[2]) || m[2] == 'Settings' || m[2] == 'History' || m[2] == 'Notifications' || m[2] == 'Template'
    )) { 
      setCurrentPage(m[2]);
      if(pages.includes(m[2])) {
        userActions.setLastSelectedStudent(m[2]);
      }
    } else {
      userActions.setPage('/projects/' + project().id + '/Summary');
    }
  });

  onMount(() => {
    const onDirResults = ({ query, results, error }) => {
      if (query === dirQuery()) {
        setDirResults(results || []);
        setDirError(error || '');
        setDirLoading(false);
      }
    };
    socket.on('directory-results', onDirResults);
    onCleanup(() => {
      socket.off('directory-results', onDirResults);
    });
  });

  createEffect(() => {
    if(currentPage() !== dirQuery()) {
      setDirResults([]);
      setDirError('');
      setDirLoading(false);
    }
  });

  createEffect(on(currentPage, () => setEditingEmail(false)));

  const onSave = (project_id, page, key, value) => {
    setUserStore('openedProject', project_id, 'pages', page, key, 'value', value);
    saveCell({
      project_id,
      page,
      key,
      value
    });
  };

  const unlock = ({project_id, page, key}) => {
    setUserStore('openedProject', project_id, 'pages', page, key, 'editable', false);
    socket.emit('project-unlock-cell', {
      project_id,
      page,
      key,
    });
  };

  const lock = ({project_id, page, key}) => {
    socket.emit('project-lock-cell', {
      project_id,
      page,
      key,
    });
  };

  const searchDirectoryFor = (name) => {
    const query = (name || '').trim();
    if (!query) return;
    setDirResults([]);
    setDirError('');
    setDirLoading(true);
    setDirQuery(query);
    socket.emit('directory-search', { query });
  };

  const openEmailModal = () => {
    setDirSearchInput(currentPage());
    setDirQuery('');
    setDirResults([]);
    setDirError('');
    setShowEmailModal(true);
  };

  const handleAddStudent = (e) => {
    e.preventDefault();
    const name = newStudentName().trim();
    if (!name) return;
    if (addNameConflict()) {
      setAddStudentError('Un étudiant porte déjà ce nom.');
      return;
    }
    setAddStudentError('');
    setStudentActionPending('add');
    socket.emit('project-add-page', {
      project_id: project().id,
      name
    }, (result) => {
      setStudentActionPending('');
      if (!result?.success) {
        setAddStudentError(result?.error || "Impossible d'ajouter cet étudiant.");
        return;
      }
      const addedName = result.name || name;
      userActions.setPage('/projects/' + project().id + '/' + addedName);
      userActions.setLastSelectedStudent(addedName);
      setCurrentPage(addedName);
      setNewStudentName('');
      setShowAddStudentModal(false);
    });
  };

  const handleRenameStudent = (e) => {
    e.preventDefault();
    const newName = renameStudentName().trim();
    const oldName = userStore.lastSelectedStudent;
    if (!newName || newName === oldName) return;
    if (renameNameConflict()) {
      setRenameStudentError('Un étudiant porte déjà ce nom.');
      return;
    }

    setRenameStudentError('');
    setStudentActionPending('rename');
    socket.emit('project-rename-page', {
      project_id: project().id,
      oldName,
      newName,
    }, (result) => {
      setStudentActionPending('');
      if (!result?.success) {
        setRenameStudentError(result?.error || "Impossible de renommer cet étudiant.");
        return;
      }
      const renamedStudent = result.name || newName;
      const studentPath = '/projects/' + project().id + '/' + renamedStudent;
      if (userStore.page !== studentPath) userActions.setPage(studentPath);
      if (userStore.lastSelectedStudent !== renamedStudent) {
        userActions.setLastSelectedStudent(renamedStudent);
      }
      setCurrentPage(renamedStudent);
      setShowRenameStudentModal(false);
    });
  };

  const handleDeleteStudent = () => {
    const name = currentPage();
    if (!project()?.pages?.[name]) return;
    if (!confirm(`Supprimer définitivement l'étudiant « ${name} » et toutes ses notes ?`)) return;

    setStudentActionPending('delete');
    socket.emit('project-remove-page', { project_id: project().id, name }, (result) => {
      setStudentActionPending('');
      if (!result?.success) {
        alert(result?.error || "Impossible de supprimer cet étudiant.");
        return;
      }

      const summaryPath = '/projects/' + project().id + '/Summary';
      userActions.setLastSelectedStudent(null);
      if (userStore.page !== summaryPath) userActions.setPage(summaryPath);
      setCurrentPage('Summary');
    });
  };

  const copyInviteLink = () => {
    const inviteUrl = location.origin + '/join?token=' + project().sharingLink;
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  const navigateToStudent = (direction) => {
    const list = studentsList();
    if (list.length === 0) return;
    const currentIndex = list.indexOf(currentPage());
    let nextIndex = 0;
    if (currentIndex !== -1) {
      nextIndex = (currentIndex + direction + list.length) % list.length;
    }
    const nextStudent = list[nextIndex];
    userActions.setPage('/projects/' + project().id + '/' + nextStudent);
    userActions.setLastSelectedStudent(nextStudent);
    setCurrentPage(nextStudent);
  };

  return (
    <div>
      <Show when={project()} fallback={<Loader message="Chargement du projet..." />}>
        
        {/* Project Header Bar */}
        <div class="project-header-bar">
          <div class="project-title-row">
            <div class="d-flex align-items-center gap-3">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                onClick={() => userActions.setPage('/')}
                title="Retour à la liste des projets"
              >
                <i class="bi bi-arrow-left"></i>
                <span class="d-none d-sm-inline">Projets</span>
              </button>
              <h1 class="project-heading">
                <span>{project().name}</span>
                <span class="project-id-badge">#{project().id}</span>
              </h1>
            </div>

            <div class="d-flex align-items-center gap-2">
              <Show when={project().connected && project().connected.length > 0}>
                <div class="live-collaborators me-2">
                  <For each={project().connected}>
                    {(username) => (
                      <span class="live-chip" title={`${username} est connecté en direct`}>
                        <span class="dot"></span>
                        {username}
                      </span>
                    )}
                  </For>
                </div>
              </Show>

              <Show when={project().owner == userStore.id}>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                  onClick={() => {
                    if (!project().sharingLink) {
                      socket.emit('create-join-link', project().id);
                    }
                    setShowInviteModal(true);
                  }}
                >
                  <i class="bi bi-person-plus-fill"></i>
                  <span>Inviter</span>
                </button>
              </Show>
            </div>
          </div>

          {/* Project Navigation Tabs */}
          <div class="project-nav-tabs">
            <button
              type="button"
              classList={{ "nav-tab-btn": true, active: currentPage() === 'Summary' }}
              onClick={() => {
                userActions.setPage('/projects/' + project().id + '/Summary');
                setCurrentPage('Summary');
              }}
            >
              <i class="bi bi-bar-chart-fill"></i>
              <span>Résumé & Notes</span>
            </button>

            <NavDropdown
              icon="bi-people-fill"
              label={`Étudiants (${studentsList().length})`}
            >
              <For each={studentsList()}>
                {(pageName) => (
                  <li>
                    <a
                      class="dropdown-item d-flex justify-content-between align-items-center"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        userActions.setPage('/projects/' + project().id + '/' + pageName);
                        userActions.setLastSelectedStudent(pageName);
                        setCurrentPage(pageName);
                      }}
                    >
                      <span>{pageName}</span>
                      <Show when={currentPage() === pageName}>
                        <i class="bi bi-check2 text-primary"></i>
                      </Show>
                    </a>
                  </li>
                )}
              </For>
              <Show when={userStore.showAdvancedActions && project().owner == userStore.id}>
                <li><hr class="dropdown-divider" /></li>
                <li>
                  <a
                    class="dropdown-item text-primary fw-medium"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setAddStudentError('');
                      setShowAddStudentModal(true);
                    }}
                  >
                    <i class="bi bi-plus-circle me-2"></i>
                    Ajouter un étudiant
                  </a>
                </li>
              </Show>
            </NavDropdown>

            <Show when={userStore.lastSelectedStudent && project().pages[userStore.lastSelectedStudent]}>
              <button
                type="button"
                classList={{ "nav-tab-btn": true, active: currentPage() === userStore.lastSelectedStudent }}
                onClick={() => {
                  userActions.setPage('/projects/' + project().id + '/' + userStore.lastSelectedStudent);
                  setCurrentPage(userStore.lastSelectedStudent);
                }}
              >
                <i class="bi bi-person-fill"></i>
                <span>{userStore.lastSelectedStudent}</span>
                <Show when={userStore.showAdvancedActions}>
                  <span
                    class="badge-edit"
                    title="Renommer l'étudiant"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenameStudentName(userStore.lastSelectedStudent);
                      setRenameStudentError('');
                      setShowRenameStudentModal(true);
                    }}
                  >
                    <i class="bi bi-pencil-fill"></i>
                  </span>
                </Show>
              </button>
            </Show>

            <div class="nav-spacer"></div>

            <button
              type="button"
              classList={{ "nav-tab-btn": true, active: currentPage() === 'History' }}
              onClick={() => {
                userActions.setPage('/projects/' + project().id + '/History');
                setCurrentPage('History');
              }}
            >
              <i class="bi bi-clock-history"></i>
              <span>Historique</span>
            </button>

            <button
              type="button"
              classList={{ "nav-tab-btn": true, active: currentPage() === 'Notifications' }}
              onClick={() => {
                userActions.setPage('/projects/' + project().id + '/Notifications');
                setCurrentPage('Notifications');
              }}
            >
              <i class="bi bi-envelope-paper-fill"></i>
              <span>Emails</span>
            </button>

            <Show when={userStore.showAdvancedActions && project().owner == userStore.id}>
              <button
                type="button"
                classList={{ "nav-tab-btn": true, active: currentPage() === 'Template' }}
                onClick={() => {
                  userActions.setPage('/projects/' + project().id + '/Template');
                  setCurrentPage('Template');
                }}
              >
                <i class="bi bi-code-slash"></i>
                <span>Modèle</span>
              </button>
            </Show>

            <button
              type="button"
              classList={{ "nav-tab-btn": true, active: currentPage() === 'Settings' }}
              onClick={() => {
                userActions.setPage('/projects/' + project().id + '/Settings');
                setCurrentPage('Settings');
              }}
            >
              <i class="bi bi-gear-fill"></i>
              <span>Paramètres</span>
            </button>

            <Show when={project().owner != userStore.id}>
              <button
                type="button"
                class="nav-tab-btn text-danger"
                onClick={() => {
                  if(!confirm("Quitter ce projet partagé ?")) return;
                  socket.emit('exit-project', project().id);
                  userActions.setPage('/');
                }}
              >
                <i class="bi bi-box-arrow-left"></i>
                <span>Quitter</span>
              </button>
            </Show>
          </div>
        </div>

        {/* Modal: Add Student */}
        <Show when={showAddStudentModal()}>
          <div class="modal show d-block" style="background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(4px);" tabindex="-1">
            <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content shadow-lg border-0 rounded-4">
                <form onSubmit={handleAddStudent}>
                  <div class="modal-header border-bottom-0 pb-0">
                    <h5 class="modal-title fw-bold">Ajouter un étudiant</h5>
                    <button type="button" class="btn-close" onClick={() => setShowAddStudentModal(false)} disabled={studentActionPending() === 'add'}></button>
                  </div>
                  <div class="modal-body py-4">
                    <div class="mb-3">
                      <label class="form-label fw-semibold">Nom de l'étudiant ou identifiant</label>
                      <input
                        type="text"
                        class="form-control"
                        classList={{ 'is-invalid': addNameConflict() || !!addStudentError() }}
                        placeholder="Ex: Jean Dupont"
                        required
                        autofocus
                        value={newStudentName()}
                        onInput={(e) => {
                          setNewStudentName(e.currentTarget.value);
                          setAddStudentError('');
                        }}
                      />
                      <Show when={addNameConflict() || addStudentError()}>
                        <div class="invalid-feedback d-block">
                          {addStudentError() || 'Un étudiant porte déjà ce nom.'}
                        </div>
                      </Show>
                    </div>
                  </div>
                  <div class="modal-footer border-top-0 pt-0">
                    <button type="button" class="btn btn-light" onClick={() => setShowAddStudentModal(false)} disabled={studentActionPending() === 'add'}>Annuler</button>
                    <button type="submit" class="btn btn-primary" disabled={!newStudentName().trim() || addNameConflict() || studentActionPending() === 'add'}>
                      {studentActionPending() === 'add' ? 'Ajout...' : 'Ajouter'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </Show>

        {/* Modal: Rename Student */}
        <Show when={showRenameStudentModal()}>
          <div class="modal show d-block" style="background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(4px);" tabindex="-1">
            <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content shadow-lg border-0 rounded-4">
                <form onSubmit={handleRenameStudent}>
                  <div class="modal-header border-bottom-0 pb-0">
                    <h5 class="modal-title fw-bold">Renommer l'étudiant</h5>
                    <button type="button" class="btn-close" onClick={() => setShowRenameStudentModal(false)} disabled={studentActionPending() === 'rename'}></button>
                  </div>
                  <div class="modal-body py-4">
                    <div class="mb-3">
                      <label class="form-label fw-semibold">Nouveau nom</label>
                      <input
                        type="text"
                        class="form-control"
                        classList={{ 'is-invalid': renameNameConflict() || !!renameStudentError() }}
                        required
                        autofocus
                        value={renameStudentName()}
                        onInput={(e) => {
                          setRenameStudentName(e.currentTarget.value);
                          setRenameStudentError('');
                        }}
                      />
                      <Show when={renameNameConflict() || renameStudentError()}>
                        <div class="invalid-feedback d-block">
                          {renameStudentError() || 'Un étudiant porte déjà ce nom.'}
                        </div>
                      </Show>
                    </div>
                  </div>
                  <div class="modal-footer border-top-0 pt-0">
                    <button type="button" class="btn btn-light" onClick={() => setShowRenameStudentModal(false)} disabled={studentActionPending() === 'rename'}>Annuler</button>
                    <button type="submit" class="btn btn-primary" disabled={!renameStudentName().trim() || renameStudentName().trim() === userStore.lastSelectedStudent || renameNameConflict() || studentActionPending() === 'rename'}>
                      {studentActionPending() === 'rename' ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </Show>

        {/* Modal: Invite Members */}
        <Show when={showInviteModal()}>
          <div class="modal show d-block" style="background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(4px);" tabindex="-1">
            <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content shadow-lg border-0 rounded-4">
                <div class="modal-header border-bottom-0 pb-0">
                  <h5 class="modal-title fw-bold">Inviter des évaluateurs</h5>
                  <button type="button" class="btn-close" onClick={() => setShowInviteModal(false)}></button>
                </div>
                <div class="modal-body py-4">
                  <p class="text-muted small mb-3">
                    Partagez ce lien avec vos collègues pour leur permettre d'évaluer ce projet simultanément en temps réel.
                  </p>
                  <Show when={project().sharingLink} fallback={<Loader message="Génération du lien..." />}>
                    <div class="input-group">
                      <input
                        type="text"
                        class="form-control"
                        readOnly
                        value={location.origin + '/join?token=' + project().sharingLink}
                      />
                      <button
                        type="button"
                        class={`btn ${copiedLink() ? 'btn-success' : 'btn-primary'}`}
                        onClick={copyInviteLink}
                      >
                        <i class={`bi ${copiedLink() ? 'bi-check-lg' : 'bi-clipboard'} me-1`}></i>
                        {copiedLink() ? 'Copié !' : 'Copier'}
                      </button>
                    </div>
                  </Show>
                </div>
                <div class="modal-footer border-top-0 pt-0">
                  <button type="button" class="btn btn-light" onClick={() => setShowInviteModal(false)}>Fermer</button>
                </div>
              </div>
            </div>
          </div>
        </Show>

        {/* Modal: Edit Student Email & LDAP Search */}
        <Show when={showEmailModal() && project().pages && project().pages[currentPage()]}>
          {(() => {
            const page = () => project().pages[currentPage()];
            let emailModalInputRef;

            createEffect(() => {
              const val = page()['_email']?.value || '';
              if (!editingEmail() && emailModalInputRef) {
                emailModalInputRef.value = val;
              }
            });

            return (
              <div class="modal show d-block" style="background: rgba(15, 23, 42, 0.4); backdrop-filter: blur(4px);" tabindex="-1">
                <div class="modal-dialog modal-dialog-centered">
                  <div class="modal-content shadow-lg border-0 rounded-4">
                    <div class="modal-header border-bottom-0 pb-0">
                      <h5 class="modal-title fw-bold d-flex align-items-center gap-2">
                        <i class="bi bi-envelope text-primary"></i>
                        <span>Email de {currentPage()}</span>
                      </h5>
                      <button type="button" class="btn-close" onClick={() => setShowEmailModal(false)}></button>
                    </div>
                    <div class="modal-body py-3">
                      <div class="mb-3">
                        <label class="form-label fw-semibold small text-muted">Adresse email de l'étudiant</label>
                        <input
                          ref={emailModalInputRef}
                          class="form-control"
                          type="email"
                          placeholder="prenom.nom@unistra.fr"
                          value={page()['_email']?.value || ''}
                          readOnly={!editingEmail()}
                          disabled={page()['_email']?.lockedBy && !editingEmail()}
                          onFocus={() => {
                            if(page()['_email']?.lockedBy) return;
                            setEditingEmail(true);
                            lock({ project_id: project().id, page: currentPage(), key: '_email' });
                          }}
                          onBlur={() => {
                            if(!editingEmail()) return;
                            setEditingEmail(false);
                            unlock({ project_id: project().id, page: currentPage(), key: '_email' });
                          }}
                          onInput={(e) => {
                            if (editingEmail()) {
                              onSave(project().id, currentPage(), '_email', e.currentTarget.value);
                            }
                          }}
                        />
                      </div>

                      <form
                        class="mb-2"
                        onSubmit={(event) => {
                          event.preventDefault();
                          searchDirectoryFor(dirSearchInput());
                        }}
                      >
                        <label for="directory-student-search" class="form-label small fw-semibold text-muted mb-1">
                          Rechercher une personne dans l'annuaire universitaire
                        </label>
                        <div class="input-group input-group-sm">
                          <input
                            id="directory-student-search"
                            type="search"
                            class="form-control"
                            placeholder="Nom, prénom ou identifiant"
                            value={dirSearchInput()}
                            onInput={(event) => setDirSearchInput(event.currentTarget.value)}
                            disabled={dirLoading()}
                          />
                          <button
                            type="submit"
                            class="btn btn-outline-primary d-inline-flex align-items-center gap-1"
                            disabled={dirLoading() || !dirSearchInput().trim()}
                          >
                            <Show when={dirLoading()} fallback={<i class="bi bi-search"></i>}>
                              <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            </Show>
                            <span>{dirLoading() ? 'Recherche...' : 'Rechercher'}</span>
                          </button>
                        </div>
                      </form>

                      <Show when={dirResults() && dirResults().length > 0}>
                        <div class="list-group shadow-sm mt-2" style="max-height: 180px; overflow-y: auto;">
                          <For each={dirResults()}>
                            {(r) => (
                              <div class="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 px-3">
                                <div>
                                  <div class="fw-semibold text-dark small">{r.name}</div>
                                  <div class="d-flex align-items-center gap-2">
                                    <span class="text-muted" style="font-size: 0.75rem;">{r.email}</span>
                                    <Show when={r.role === 'student'}>
                                      <span class="badge text-bg-light border text-secondary" style="font-size: 0.65rem;">Étudiant</span>
                                    </Show>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  class="btn btn-sm btn-primary py-0 px-2"
                                  onClick={() => {
                                    onSave(project().id, currentPage(), '_email', r.email);
                                    setDirResults([]);
                                    setShowEmailModal(false);
                                  }}
                                >
                                  Choisir
                                </button>
                              </div>
                            )}
                          </For>
                        </div>
                      </Show>

                      <Show when={!dirLoading() && !!dirError()}>
                        <div class="alert alert-warning py-2 px-3 mt-2 mb-0 small" role="status">
                          <i class="bi bi-exclamation-triangle-fill me-2"></i>
                          {dirError()}
                        </div>
                      </Show>

                      <Show when={!dirLoading() && !!dirQuery() && !dirError() && dirResults().length === 0}>
                        <div class="text-muted small mt-2">
                          Aucun résultat trouvé pour « {dirQuery()} ». Vous pouvez saisir l'adresse email manuellement.
                        </div>
                      </Show>
                    </div>
                    <div class="modal-footer border-top-0 pt-0">
                      <button type="button" class="btn btn-primary" onClick={() => setShowEmailModal(false)}>Terminer</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </Show>

        {/* Sub-views */}
        <Show when={currentPage() === 'Summary'}>
          <ProjectSummary project={project()} />
        </Show>
        <Show when={currentPage() === 'Settings'}>
          <ProjectSettings project={project()} />
        </Show>
        <Show when={currentPage() === 'Template'}>
          <TemplateEditor project={project()} />
        </Show>
        <Show when={currentPage() === 'Notifications'}>
          <ProjectNotifications project={project()} />
        </Show>
        <Show when={currentPage() === 'History'}>
          <ProjectHistory project={project()} />
        </Show>

        {/* Student Evaluation Form */}
        <Show when={project().pages && project().pages[currentPage()]}>
          {(page) => (
            <div class="grading-container">
              {/* Condensed Student Header Bar */}
              <div class="student-header-bar">
                <div class="student-title-area">
                  <h2 class="student-name">{currentPage()}</h2>

                  {/* Compact email button trigger */}
                  <button
                    type="button"
                    class={`student-email-btn ${page()['_email']?.value ? '' : 'no-email'}`}
                    onClick={openEmailModal}
                    title="Cliquer pour renseigner ou modifier l'adresse email"
                  >
                    <i class={`bi ${page()['_email']?.value ? 'bi-envelope-check-fill text-success' : 'bi-envelope-plus text-muted'}`}></i>
                    <span>{page()['_email']?.value || 'Ajouter un email'}</span>
                    <i class="bi bi-pencil-square text-muted" style="font-size: 0.75rem;"></i>
                  </button>

                  <div class={`student-total-grade student-grade-badge ${getScoreBadgeClass(studentGrades().total)}`}>
                    <span>Note globale</span>
                    <strong>{studentGrades().total} / 20</strong>
                  </div>
                </div>

                <div class="d-flex align-items-center gap-1">
                  <Show when={userStore.showAdvancedActions && project().owner == userStore.id}>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1 me-1"
                      onClick={handleDeleteStudent}
                      disabled={studentActionPending() === 'delete'}
                      title="Supprimer définitivement cet étudiant"
                    >
                      <i class="bi bi-trash3-fill"></i>
                      <span>{studentActionPending() === 'delete' ? 'Suppression...' : 'Supprimer'}</span>
                    </button>
                  </Show>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                    onClick={() => navigateToStudent(-1)}
                    title="Étudiant précédent"
                  >
                    <i class="bi bi-chevron-left"></i>
                    <span>Précédent</span>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                    onClick={() => navigateToStudent(1)}
                    title="Étudiant suivant"
                  >
                    <span>Suivant</span>
                    <i class="bi bi-chevron-right"></i>
                  </button>
                </div>
              </div>

              {/* Condensed Unified Evaluation Grid */}
              <div class="eval-grid">
                <div class="eval-grid-header">
                  <div>Critère d'évaluation</div>
                  <div class="text-center">Note (/4)</div>
                  <div>Appréciation & Feedback</div>
                </div>

                <For each={project().student_template}>
                  {(section) => (
                    <>
                      <div class="eval-section-divider">
                        <span class="eval-section-title">
                          <i class="bi bi-chevron-right text-primary" style="font-size: 0.75rem;"></i>
                          <span>{section.name}</span>
                        </span>
                        <span class={`student-grade-badge ${getScoreBadgeClass(studentGrades().sections[section.name])}`}>
                          {studentGrades().sections[section.name]} / 20
                        </span>
                      </div>

                      <For each={section.cells}>
                        {(element) => (
                          <Switch>
                            <Match when={element.type === 'input-text'}>
                              <Cell
                                onSave={onSave}
                                unlock={unlock}
                                lock={lock}
                                project_id={project().id}
                                page={currentPage()}
                                key_comment={element.key + ':comment'}
                                key_note={element.key + ':note'}
                                label={element.label}
                                cell_comment={page()[element.key + ':comment'] || { value: '', lockedBy: false, editable: true }}
                                cell_note={page()[element.key + ':note'] || { value: '', lockedBy: false, editable: true }}
                              />
                            </Match>
                          </Switch>
                        )}
                      </For>
                    </>
                  )}
                </For>
              </div>
            </div>
          )}
        </Show>
      </Show>
    </div>
  );
};

// Inline editor for project templates (owner/admin in advanced mode)
const TemplateEditor = (props) => {
  const [studentTpl, setStudentTpl] = createSignal(props.project.raw_student_template || '');
  const [summaryTpl, setSummaryTpl] = createSignal(props.project.summary_template || '');
  const [saving, setSaving] = createSignal(false);
  const [saved, setSaved] = createSignal(false);

  createEffect(() => {
    setStudentTpl(props.project.raw_student_template || '');
    setSummaryTpl(props.project.summary_template || '');
  });

  const save = () => {
    setSaving(true);
    setSaved(false);
    socket.emit('project-update-template', {
      project_id: props.project.id,
      student_template: studentTpl(),
      summary_template: summaryTpl()
    });
    setTimeout(() => { setSaving(false); setSaved(true); }, 400);
  };

  return (
    <div class="card-panel">
      <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
        <div>
          <h3 class="h5 fw-bold mb-1">Édition des Modèles (Templates)</h3>
          <p class="text-muted small mb-0">Personnalisez la grille de critères et les formules de calcul des notes.</p>
        </div>
        <button
          type="button"
          class="btn btn-primary d-inline-flex align-items-center gap-2 shadow-sm"
          disabled={saving()}
          onClick={save}
        >
          <Show when={saving()} fallback={<i class="bi bi-floppy-fill"></i>}>
            <div class="spinner-border spinner-border-sm" role="status"></div>
          </Show>
          <span>{saving() ? 'Enregistrement…' : 'Enregistrer le modèle'}</span>
        </button>
      </div>

      <Show when={saved()}>
        <div class="alert alert-success d-flex align-items-center gap-2 mb-3">
          <i class="bi bi-check-circle-fill"></i>
          <span>Modèle mis à jour avec succès !</span>
        </div>
      </Show>

      <div class="row g-4">
        <div class="col-lg-7">
          <div class="mb-3">
            <label class="form-label fw-semibold">Modèle des critères étudiants (format Markdown-like)</label>
            <textarea
              class="form-control font-monospace"
              rows={14}
              value={studentTpl()}
              onInput={(e) => setStudentTpl(e.currentTarget.value)}
            />
            <div class="form-text mt-2">
              Utilisez <code># Section = uniform(/4)</code> pour une section avec moyenne uniforme sur 20, et <code>- Critère</code> pour chaque item.
            </div>
          </div>
        </div>

        <div class="col-lg-5">
          <div class="mb-3">
            <label class="form-label fw-semibold">Formule du résumé / Note totale</label>
            <textarea
              class="form-control font-monospace"
              rows={5}
              value={summaryTpl()}
              onInput={(e) => setSummaryTpl(e.currentTarget.value)}
            />
            <div class="form-text mt-2">
              Exemple : <code>note: (&#123;Travail&#125;*3 + &#123;Rapport&#125; + &#123;Présentation&#125;)/5</code>
            </div>
          </div>

          <div class="p-3 bg-light rounded-3 border">
            <h6 class="fw-bold mb-2"><i class="bi bi-info-circle me-1 text-primary"></i> Aide & Syntaxe</h6>
            <ul class="small text-muted ps-3 mb-0">
              <li>Chaque ligne <code># Nom = formule</code> définit une note de section.</li>
              <li>Chaque ligne <code>- Intitulé</code> définit un sous-critère noté sur 4.</li>
              <li><code>uniform(/4)</code> calcule automatiquement la moyenne ramenée sur 20.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
